import { Fragment, useMemo, useState } from "react";
import { useResource, formatDate, formatDateTime, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import type { Employee, FormSubmission, FormTemplate, Participant } from "../lib/types";

const templateChip = (s: FormTemplate["status"]) =>
    s === "published" ? "pass" : s === "draft" ? "warn" : "mute";

const submissionChip = (s: FormSubmission["status"]) => (s === "new" ? "warn" : "pass");

/** Templates carry created_at at runtime; the shared type may omit it. */
type Template = FormTemplate & { created_at?: string };

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; template: FormTemplate }
    | { kind: "fill"; template: FormTemplate }
    | null;

export const Forms = () => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";
    const canReview = canManage || user?.role === "supervisor";

    const templates = useResource<FormTemplate[]>("/form-templates");
    const submissions = useResource<FormSubmission[]>("/form-submissions");
    const participants = useResource<Participant[]>("/participants");
    const employees = useResource<Employee[]>("/employees");

    const [tab, setTab] = useState<"templates" | "submissions">("templates");
    const [statusFilter, setStatusFilter] = useState("");
    const [templateFilter, setTemplateFilter] = useState("");
    const [expanded, setExpanded] = useState<string | null>(null);
    const [dialog, setDialog] = useState<Dialog>(null);

    // Fill-a-form state: answers keyed by field label, snapshotted into jsonb.
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [fillParticipant, setFillParticipant] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const templateName = useMemo(() => {
        const m = new Map((templates.data ?? []).map((t) => [t.id, `${t.code} — ${t.name}`]));
        return (id: string) => m.get(id) ?? "—";
    }, [templates.data]);

    const employeeName = useMemo(() => {
        const m = new Map((employees.data ?? []).map((e) => [e.id, e.full_name]));
        return (id: string | null) => (id ? m.get(id) ?? "—" : "—");
    }, [employees.data]);

    const participantName = useMemo(() => {
        const m = new Map((participants.data ?? []).map((p) => [p.id, p.full_name]));
        return (id: string | null) => (id ? m.get(id) ?? "—" : "—");
    }, [participants.data]);

    const visibleTemplates = (templates.data ?? []).filter(
        (t) => !statusFilter || t.status === statusFilter
    );
    const visibleSubmissions = (submissions.data ?? []).filter(
        (s) => !templateFilter || s.template_id === templateFilter
    );

    const openFill = (template: FormTemplate) => {
        setAnswers({});
        setFillParticipant("");
        setError(null);
        setDialog({ kind: "fill", template });
    };

    const submitForm = async () => {
        if (dialog?.kind !== "fill") return;
        const missing = dialog.template.fields.filter((f) => f.required && !answers[f.label]?.trim());
        if (missing.length) {
            setError(`Required: ${missing.map((f) => f.label).join(", ")}`);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const first = Object.values(answers).find((v) => v.trim());
            await api.post("/form-submissions", {
                template_id: dialog.template.id,
                participant_id: fillParticipant || null,
                summary: (first ?? dialog.template.name).slice(0, 160),
                answers,
            });
            setDialog(null);
            submissions.reload();
            setTab("submissions");
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Could not submit form");
        } finally {
            setSaving(false);
        }
    };

    const markReviewed = async (submission: FormSubmission) => {
        await api.put(`/form-submissions/${submission.id}`, {
            status: "reviewed",
            reviewed_by: user?.id ?? null,
            reviewed_at: new Date().toISOString(),
        });
        submissions.reload();
    };

    const setStatus = async (template: FormTemplate, status: FormTemplate["status"]) => {
        await api.put(`/form-templates/${template.id}`, { status });
        templates.reload();
    };

    return (
        <div>
            <div className="tabs">
                <button className={tab === "templates" ? "active" : ""} onClick={() => setTab("templates")}>
                    Templates ({(templates.data ?? []).length})
                </button>
                <button className={tab === "submissions" ? "active" : ""} onClick={() => setTab("submissions")}>
                    Submissions ({(submissions.data ?? []).filter((s) => s.status === "new").length} new)
                </button>
                <div className="spacer" />
                {canManage && tab === "templates" && (
                    <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ New template</button>
                )}
            </div>

            {tab === "templates" && (
                <div className="card">
                    <div className="card-body flush">
                        <div className="toolbar" style={{ marginBottom: "0.6rem" }}>
                            <label>Status&nbsp;
                                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="">All statuses</option>
                                    <option value="published">Published</option>
                                    <option value="draft">Draft</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </label>
                            <div className="spacer" />
                            <span className="muted">{visibleTemplates.length} template{visibleTemplates.length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr><th>Code</th><th>Name</th><th>Status</th><th>Entries</th><th>Assigned to</th><th>Fields</th><th>Created</th><th /></tr>
                                </thead>
                                <tbody>
                                    {visibleTemplates.map((t) => (
                                        <tr key={t.id}>
                                            <td>{t.code}</td>
                                            <td>{t.name}</td>
                                            <td><span className={`chip ${templateChip(t.status)}`}>{titleCase(t.status)}</span></td>
                                            <td>
                                                <button className="icon-btn" title="View submissions"
                                                    onClick={() => { setTemplateFilter(t.id); setTab("submissions"); }}>
                                                    {(submissions.data ?? []).filter((s) => s.template_id === t.id).length}
                                                </button>
                                            </td>
                                            <td>{t.audience}</td>
                                            <td>{t.fields.length}</td>
                                            <td>{(t as Template).created_at ? formatDate((t as Template).created_at!) : "—"}</td>
                                            <td>
                                                <div className="row-actions">
                                                    {t.status === "published" && (
                                                        <button className="icon-btn" onClick={() => openFill(t)}>Fill in</button>
                                                    )}
                                                    {canManage && (
                                                        <>
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "edit", template: t })}>Edit</button>
                                                            {t.status === "draft" && (
                                                                <button className="icon-btn" onClick={() => void setStatus(t, "published")}>Publish</button>
                                                            )}
                                                            {t.status === "published" && (
                                                                <button className="icon-btn" onClick={() => void setStatus(t, "archived")}>Archive</button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {!visibleTemplates.length && (
                                        <tr><td colSpan={8} className="empty">No templates match.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "submissions" && (
                <div className="card">
                    <div className="card-body flush">
                        <div className="toolbar" style={{ marginBottom: "0.6rem" }}>
                            <label>Form&nbsp;
                                <select value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)}>
                                    <option value="">All forms</option>
                                    {(templates.data ?? []).map((t) => (
                                        <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="spacer" />
                            <span className="muted">{visibleSubmissions.length} submission{visibleSubmissions.length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr><th>Submitted</th><th>Form</th><th>By</th><th>Participant</th><th>Summary</th><th>Status</th><th /></tr>
                                </thead>
                                <tbody>
                                    {visibleSubmissions.map((s) => (
                                        <Fragment key={s.id}>
                                            <tr onClick={() => setExpanded(expanded === s.id ? null : s.id)} style={{ cursor: "pointer" }}>
                                                <td>{formatDateTime(s.created_at)}</td>
                                                <td>{templateName(s.template_id)}</td>
                                                <td>{employeeName(s.submitted_by)}</td>
                                                <td>{participantName(s.participant_id)}</td>
                                                <td>{s.summary}</td>
                                                <td><span className={`chip ${submissionChip(s.status)}`}>{titleCase(s.status)}</span></td>
                                                <td>
                                                    {canReview && s.status === "new" && (
                                                        <button className="icon-btn" onClick={(e) => { e.stopPropagation(); void markReviewed(s); }}>
                                                            Mark reviewed
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {expanded === s.id && (
                                                <tr>
                                                    <td colSpan={7}>
                                                        {Object.entries(s.answers).map(([label, value]) => (
                                                            <p key={label}><b>{label}:</b> {value}</p>
                                                        ))}
                                                        {s.reviewed_at && (
                                                            <p className="sub">Reviewed {formatDateTime(s.reviewed_at)} by {employeeName(s.reviewed_by)}</p>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    ))}
                                    {!visibleSubmissions.length && (
                                        <tr><td colSpan={7} className="empty">No submissions match.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {(dialog?.kind === "create" || dialog?.kind === "edit") && (
                <TemplateBuilder
                    template={dialog.kind === "edit" ? dialog.template : null}
                    onClose={() => setDialog(null)}
                    onSaved={() => { setDialog(null); templates.reload(); }}
                />
            )}

            {dialog?.kind === "fill" && (
                <Modal
                    title={`${dialog.template.code} — ${dialog.template.name}`}
                    onClose={() => setDialog(null)}
                    footer={
                        <>
                            <button className="btn" onClick={() => setDialog(null)}>Cancel</button>
                            <button className="btn" disabled={saving} onClick={() => void submitForm()}>
                                {saving ? "Submitting…" : "Submit"}
                            </button>
                        </>
                    }
                >
                    {error && <div className="error-banner">{error}</div>}
                    <div className="field">
                        <label>Participant (optional)</label>
                        <select value={fillParticipant} onChange={(e) => setFillParticipant(e.target.value)}>
                            <option value="">Not participant-related</option>
                            {(participants.data ?? []).map((p) => (
                                <option key={p.id} value={p.id}>{p.full_name}</option>
                            ))}
                        </select>
                    </div>
                    {dialog.template.fields.map((f) => (
                        <div className="field" key={f.label}>
                            <label>{f.label}{f.required ? " *" : ""}</label>
                            {f.kind === "textarea" ? (
                                <textarea
                                    value={answers[f.label] ?? ""}
                                    onChange={(e) => setAnswers({ ...answers, [f.label]: e.target.value })}
                                />
                            ) : (
                                <input
                                    value={answers[f.label] ?? ""}
                                    onChange={(e) => setAnswers({ ...answers, [f.label]: e.target.value })}
                                />
                            )}
                        </div>
                    ))}
                </Modal>
            )}
        </div>
    );
};

interface DraftField { label: string; kind: "text" | "textarea" | "select"; required: boolean; }

/**
 * Form-builder-lite: the template's details PLUS its actual fields — add,
 * relabel, retype, require and remove questions. The prototype's "+ New form"
 * was a coming-soon stub; this one really builds forms.
 */
function TemplateBuilder({ template, onClose, onSaved }: {
    template: FormTemplate | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [code, setCode] = useState(template?.code ?? "");
    const [name, setName] = useState(template?.name ?? "");
    const [audience, setAudience] = useState(template?.audience ?? "All staff");
    const [category, setCategory] = useState(template?.category ?? "Other");
    const [status, setStatus] = useState(template?.status ?? "draft");
    const [description, setDescription] = useState(template?.description ?? "");
    const [fields, setFields] = useState<DraftField[]>(
        (template?.fields ?? []).map((f) => ({
            label: f.label, kind: f.kind === "textarea" ? "textarea" : "text", required: !!f.required,
        }))
    );
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const setField = (i: number, patch: Partial<DraftField>) =>
        setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));

    const save = async () => {
        if (!code.trim() || !name.trim()) { setError("Code and name are required."); return; }
        const clean = fields.filter((f) => f.label.trim());
        if (!clean.length) { setError("Add at least one question."); return; }
        setBusy(true); setError(null);
        try {
            const body = {
                code: code.trim(), name: name.trim(), audience: audience.trim() || "All staff",
                category, status, description: description.trim() || null,
                fields: clean.map((f) => ({ label: f.label.trim(), kind: f.kind, required: f.required })),
            };
            if (template) await api.put(`/form-templates/${template.id}`, body);
            else await api.post("/form-templates", body);
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save the template");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={template ? `Edit ${template.code}` : "New form"}
            subtitle="Submissions snapshot their answers, so editing a form never corrupts what was already filled in."
            width="wide"
            onClose={onClose}
            footer={
                <>
                    <button className="btn" disabled={busy} onClick={() => void save()}>
                        {busy ? "Saving…" : template ? "Save form" : "Create form"}
                    </button>
                    <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
                </>
            }
        >
            {error && <div className="error-banner">{error}</div>}
            <div className="form-grid">
                <div className="field half">
                    <label>Form code <span className="req">*</span></label>
                    <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="TC-FRM-0XX" />
                </div>
                <div className="field half">
                    <label>Form name <span className="req">*</span></label>
                    <input value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="field half">
                    <label>Assigned to</label>
                    <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="All staff" />
                </div>
                <div className="field half">
                    <label>Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value as FormTemplate["status"])}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                    </select>
                </div>
                <div className="field half">
                    <label>Category</label>
                    <select value={category} onChange={(e) => setCategory(e.target.value)}>
                        {["Participant Assessment", "Medication", "High Care",
                            "Injury/Incident Management", "Exit/Transition", "Onboarding Update", "Other"]
                            .map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="field">
                    <label>Description</label>
                    <textarea value={description ?? ""} onChange={(e) => setDescription(e.target.value)} />
                </div>
            </div>

            <h3 style={{ margin: "1rem 0 0.4rem" }}>Questions</h3>
            <div className="table-wrap">
                <table>
                    <thead>
                        <tr><th style={{ minWidth: 220 }}>Question</th><th>Answer type</th><th>Required</th><th /></tr>
                    </thead>
                    <tbody>
                        {fields.map((f, i) => (
                            <tr key={i}>
                                <td><input value={f.label} placeholder="What happened?"
                                    onChange={(e) => setField(i, { label: e.target.value })} /></td>
                                <td>
                                    <select value={f.kind} onChange={(e) => setField(i, { kind: e.target.value as DraftField["kind"] })}>
                                        <option value="text">Short answer</option>
                                        <option value="textarea">Long answer</option>
                                    </select>
                                </td>
                                <td>
                                    <label className="check">
                                        <input type="checkbox" checked={f.required}
                                            onChange={(e) => setField(i, { required: e.target.checked })} />
                                    </label>
                                </td>
                                <td><button className="icon-btn danger" title="Remove question"
                                    onClick={() => setFields((fs) => fs.filter((_, j) => j !== i))}>✕</button></td>
                            </tr>
                        ))}
                        {!fields.length && (
                            <tr><td colSpan={4} className="empty">No questions yet — add one below.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <button className="btn ghost small" style={{ marginTop: 6 }}
                onClick={() => setFields((fs) => [...fs, { label: "", kind: "textarea", required: false }])}>
                ＋ Add question
            </button>
        </Modal>
    );
}
